import React, {
  MouseEvent,
} from 'react';
import {
  Row,
  ColumnDef,
} from '@tanstack/react-table';
import Tooltip from 'core/components/Tooltip';
import IconButton from '@mui/material/IconButton';
import Name from 'pages/Dashboard/components/Name';
import {
  FaxContactDTO,
} from 'dtos';
import {
  DeleteIcon,
  FaxIcon,
} from 'core/icons';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import {
  faxEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useDeleteContact,
} from 'pages/Dashboard/pages/Fax/hooks';

type ActionsCellProps = {
  row: Row<FaxContactDTO>;
};

function ActionsCell({ row }: ActionsCellProps) {
  const {
    mutateAsync: deleteContact,
  } = useDeleteContact();
  const notify = useNotify();
  const { confirm, closeConfirm } = useConfirmDialog();

  const handleDelete = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    confirm({
      title: 'Delete Contact',
      confirmText: 'Confirm',
      content: 'Are you sure you want to delete this contact?',
      onConfirm: async () => {
        await deleteContact({ faxContactId: row?.original?.faxContactId ?? 0 });
        closeConfirm();
        faxEventChannel.emit('onContactChange');
        notify({
          variant: 'success',
          message: 'Contact Deleted Successfully',
        });
      },
    });
  };

  return (
    <div className="flex justify-start w-full">
      <Tooltip title="Send Fax">
        <IconButton
          aria-label="Send Fax"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            faxEventChannel.emit('onOpenSendFax', { contact: row.original });
          }}
          color="info"
        >
          <FaxIcon className="!w-5 !h-5 !text-black" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete Contact">
        <IconButton
          aria-label="Delete Contact"
          size="small"
          className="!p-1"
          onClick={handleDelete}
          color="error"
        >
          <DeleteIcon className="!text-xl text-black" />
        </IconButton>
      </Tooltip>
    </div>
  );
}

const columns: ColumnDef<FaxContactDTO>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    size: 260,
    cell: ({ row }) => (
      <Name
        name={row?.original?.name ?? ''}
        image
      />
    ),
  }, {
    id: 'faxNumber',
    accessorKey: 'faxNumber',
    header: 'Fax Number',
    meta: { autoSized: true },
  }, {
    id: 'actions',
    header: '',
    size: 80,
    cell: ({ row }) => <ActionsCell row={row} />,
  },
];

export default columns;
